import React from 'react';
import { BadgeProps } from '@frontend/shadcn/components/ui/badge';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Tooltip, TooltipTrigger, TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import {
  TooltipHeader,
  TooltipTitle,
  TooltipBody,
  TooltipItem,
  TooltipItemKey,
  TooltipItemValue,
  TooltipContent,
} from '../../../components/tooltip';
import { DismissableBadge } from '../../../components/filters/badges/dismissable-badge';

interface AdCampaign {
  id: string;
  name: string;
  adSets?: AdSet[];
}

interface AdSet {
  id: string;
  name: string;
}

interface AdCampaignBadgesProps {
  data: {
    campaigns: AdCampaign[];
    adSets: AdSet[];
  };
  onDismiss: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

type CombinedProps = BadgeProps & AdCampaignBadgesProps;

const AdCampaignBadges = React.forwardRef<HTMLDivElement, CombinedProps>(
  ({ children, className, data, onDismiss, ...props }, ref) => {
    const { campaigns, adSets } = data;
    const totalSelected = campaigns.length + adSets.length;

    // Do not display a badge when no campaigns or ad sets are selected
    if (totalSelected === 0) {
      return null;
    }

    const displayText =
      campaigns.length > 0
        ? `Campaign: ${campaigns[0].name}${campaigns.length > 1 ? ` +${campaigns.length - 1}` : ''}`
        : `Ad Set: ${adSets[0].name}${adSets.length > 1 ? ` +${adSets.length - 1}` : ''}`;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <DismissableBadge
                ref={ref}
                key={`campaigns-${totalSelected}`}
                variant="secondary"
                className="mr-2 mb-2"
                aria-label="Ad campaign filter"
                role="button"
                data-dd-action-name="open-ads-filters"
                {...props}
              >
                {displayText}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDismiss(e);
                  }}
                  aria-label="Remove ad campaign filter"
                  className="flex ml-1 p-1 focus:outline-none focus:ring"
                  data-dd-action-name="remove-ads-filters"
                >
                  <Cross2Icon className="h-3 w-3" />
                </button>
                <span className="sr-only">Remove ad campaign filter</span>
              </DismissableBadge>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <TooltipHeader>
              <TooltipTitle>Ad Campaign Filter</TooltipTitle>
            </TooltipHeader>
            <TooltipBody>
              {campaigns.length > 0 && (
                <TooltipItem>
                  <TooltipItemKey>Selected Campaigns</TooltipItemKey>
                  <TooltipItemValue>{campaigns.map((campaign) => campaign.name).join(', ')}</TooltipItemValue>
                </TooltipItem>
              )}
              {adSets.length > 0 && (
                <TooltipItem>
                  <TooltipItemKey>Selected Ad Sets</TooltipItemKey>
                  <TooltipItemValue>{adSets.map((adSet) => adSet.name).join(', ')}</TooltipItemValue>
                </TooltipItem>
              )}
            </TooltipBody>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);

AdCampaignBadges.displayName = 'AdCampaignBadges';

export { AdCampaignBadges };
